import { useEffect, useState } from 'react';
import ReportStatusBadge from '../../../../utils/ReportStatusBadge';
import { Link } from 'react-router-dom';
import Paginator from '../../../../utils/Paginator';
import { routesMap } from '../../../../routes/AppRoutes';
import { ElementRead } from '../../../../api/apiTypes';
import { Eye } from 'lucide-react';

type ContainerListaDriverPageProps = {
  intermediari: ElementRead[];
  driverRoute: string | undefined;
  reportId: string;
  id: string;
  driverId: string;
};

const ContainerBestExecution = ({ reportId, intermediari, driverRoute, id, driverId }: ContainerListaDriverPageProps) => {
  // ✅ Leggiamo la pagina corrente da localStorage solo una volta
  const [currentPage, setCurrentPage] = useState(() => {
    const storedPage = localStorage.getItem('currentPage_best_execution');
    return storedPage ? parseInt(storedPage, 10) : 1;
  });

  const itemsPerPage = 6;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    localStorage.setItem('currentPage_best_execution', page.toString());
  };

  useEffect(() => {
    if (!localStorage.getItem('currentPage_best_execution')) {
      localStorage.setItem('currentPage_best_execution', '1');
      setCurrentPage(1);
    }
  }, [driverRoute, driverId, reportId]);

  // ✅ Sincronizza dati se servono
  const getSyncedIntermediari = (intermediari: ElementRead[]): ElementRead[] => {
    return intermediari.map((intermediario) => {
      const stored = localStorage.getItem(`intermediario_${driverRoute}_${intermediario.id}`);
      return stored ? JSON.parse(stored) : intermediario;
    });
  };

  const syncedIntermediari = getSyncedIntermediari(intermediari);

  const totalPages = Math.ceil(syncedIntermediari.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedIntermediari = syncedIntermediari.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="container-lista-report-page">
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Header tabella */}
        <div className="grid grid-cols-3 md:grid-cols-12 gap-y-4 gap-x-2 px-6 py-3.5 border-b border-gray-100 bg-gray-50 font-semibold text-[#1B365D] text-xs">
          <div className="col-span-1 md:col-span-2 flex items-center">Identificativo</div>
          <div className="col-span-1 md:col-span-1 flex justify-center items-center">BE statica</div>
          <div className="col-span-1 md:col-span-2 flex justify-center items-center">BE statica + Dinamica</div>
          <div className="col-span-1 md:col-span-1 flex justify-center items-center">BE Dinamica</div>
          <div className="col-span-1 md:col-span-2 flex justify-center items-center">Integrazioni Tecniche</div>
          <div className="col-span-1 md:col-span-1 flex justify-center items-center text-center">Score</div>
          <div className="col-span-1 md:col-span-1 flex justify-center items-center text-center">Stato</div>
          <div className="col-span-1 md:col-span-1 flex justify-center items-center text-center">Validazione</div>
          <div className="hidden md:block md:col-span-1" />
        </div>

        {/* Lista */}
        <div className="divide-y divide-gray-100">
          {paginatedIntermediari.map((intermediario) => {
            const intermediarioId = intermediario.id;
            return (
              <div
                key={intermediarioId}
                className="grid grid-cols-3 md:grid-cols-12 gap-y-4 gap-x-2 px-6 py-4 hover:bg-gray-50/50 transition duration-150 ease-in-out items-center text-xs"
              >
                <div className="col-span-1 md:col-span-2 font-medium text-gray-800">{intermediario.name}</div>
                <div className="col-span-1 md:col-span-1 flex justify-center items-center text-[#1B365D] font-semibold">
                  {intermediario.value?.result_values?.includes('static') ? '✓' : ''}
                </div>
                <div className="col-span-1 md:col-span-2 flex justify-center items-center text-[#1B365D] font-semibold">
                  {intermediario.value?.result_values?.includes('mixed') ? '✓' : ''}
                </div>
                <div className="col-span-1 md:col-span-1 flex justify-center items-center text-[#1B365D] font-semibold">
                  {intermediario.value?.result_values?.includes('dynamic') ? '✓' : ''}
                </div>
                <div className="col-span-1 md:col-span-2 flex justify-center items-center text-[#1B365D] font-semibold">
                  {intermediario.value?.result_values?.includes('advanced-features') ? '✓' : ''}
                </div>
                <div className="col-span-1 md:col-span-1 text-center text-gray-600">
                  {intermediario.value.score?.total_score}
                </div>
                <div className="col-span-1 md:col-span-1 text-center">
                  <ReportStatusBadge stato={intermediario.status} />
                </div>
                <div className="col-span-1 md:col-span-1 text-center">
                  <ReportStatusBadge stato={intermediario.validation} />
                </div>
                <div className="col-span-1 md:col-span-1 flex justify-end">
                  <Link
                    to={routesMap.VISUALIZZA_INTERMEDIARIO(reportId, driverRoute ?? '', intermediario.name)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1B365D] text-xs font-medium text-white hover:bg-[#142a4a] transition-colors"
                    state={{ id, driverId, intermediarioId }}
                  >
                     <Eye className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* Paginazione */}
        {totalPages > 0 && (
          <div className="p-4">
            <Paginator
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ContainerBestExecution;
